import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { X, Copy, Check, Building2, Users, Search } from 'lucide-react';

const OrganizationSettingsModal = ({ onClose, onUpdate }) => {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/admin/organization`);
      setOrganization(response.data);
    } catch (error) {
      toast.error('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const copyJoinCode = () => {
    navigator.clipboard.writeText(organization.join_code);
    setCopied(true);
    toast.success('Join code copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const searchUsers = async () => {
    if (!searchEmail || searchEmail.length < 3) {
      toast.error('Please enter at least 3 characters');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?email=${searchEmail}`);
      setSearchResults(response.data.users);
      if (response.data.users.length === 0) {
        toast.info('No users found without organization');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addUserToOrg = async (userId) => {
    try {
      await axios.post(`${API}/admin/organization/add-user`, { user_id: userId });
      toast.success('User added to organization!');
      setSearchResults(searchResults.filter(u => u.id !== userId));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="max-w-2xl w-full">
          <CardContent className="py-12 text-center">
            Loading organization settings...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="max-w-2xl w-full">
          <CardContent className="py-12 text-center">
            <p className="text-gray-600">No organization found</p>
            <Button onClick={onClose} className="mt-4">Close</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          data-testid="close-org-settings"
        >
          <X className="w-6 h-6" />
        </button>

        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Building2 className="w-6 h-6" />
            Organization Settings
          </CardTitle>
          <CardDescription>Manage your organization and invite volunteers</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Organization Info */}
          <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200">
            <CardHeader>
              <CardTitle className="text-xl">{organization.name}</CardTitle>
              <CardDescription>Your organization details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Join Code</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-white border-2 border-indigo-300 rounded-lg">
                      <code className="text-2xl font-bold text-indigo-600 tracking-wider">
                        {organization.join_code}
                      </code>
                    </div>
                    <Button
                      onClick={copyJoinCode}
                      variant="outline"
                      size="icon"
                      className="h-12 w-12"
                      data-testid="copy-join-code"
                    >
                      {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Share this code with volunteers so they can join your organization during registration
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Add Existing Users */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Add Existing Users
              </CardTitle>
              <CardDescription>Search for users who registered individually and add them to your organization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by email..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                    data-testid="search-user-email"
                  />
                  <Button
                    onClick={searchUsers}
                    disabled={searching}
                    data-testid="search-users-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {searchResults.map(user => (
                      <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <div className="font-semibold">{user.name}</div>
                          <div className="text-sm text-gray-600">{user.email}</div>
                        </div>
                        <Button
                          onClick={() => addUserToOrg(user.id)}
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`add-user-${user.id}`}
                        >
                          Add to Organization
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">How to Add Volunteers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p><strong>Method 1: Share Join Code</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Give volunteers your join code: <code className="bg-white px-2 py-1 rounded">{organization.join_code}</code></li>
                  <li>During registration, they check "I have an organization code"</li>
                  <li>They enter the code and automatically join your organization</li>
                </ul>
                
                <p className="mt-4"><strong>Method 2: Add Existing Users</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Search for users by email address</li>
                  <li>Click "Add to Organization" to bring them into your org</li>
                  <li>Only works for users who don't have an organization yet</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationSettingsModal;
